import React, { useState, useEffect } from 'react';
import styled from 'styled-components';
import { useNavigate } from 'react-router-dom';
import {
  MdAdd, MdSearch, MdShoppingCart, MdWorkspaces,
  MdVisibility, MdPendingActions, MdPlayArrow, MdCheckCircle, MdCancel
} from 'react-icons/md';
import { pedidosAPI } from '../services/api';
import { useModal } from '../context/ModalContext';
import { useConfig } from '../context/ConfigContext';
import { SkeletonTableRows, EmptyState } from '../components/SkeletonLoader';

/* ─── Styled ─── */
const Container = styled.div`max-width: 1400px;`;

const PageHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: ${p => p.theme.spacing.xl};
  gap: ${p => p.theme.spacing.md};
  flex-wrap: wrap;
`;

const TitleGroup = styled.div``;
const Title = styled.h1`
  font-size: ${p => p.theme.fontSizes.xxxl};
  color: ${p => p.theme.colors.textPrimary};
  margin: 0 0 4px 0;
  display: flex; align-items: center; gap: 10px;
  svg { color: ${p => p.theme.colors.primary}; }
`;
const Subtitle = styled.p`font-size: ${p => p.theme.fontSizes.sm}; color: ${p => p.theme.colors.textSecondary}; margin: 0;`;

const AddBtn = styled.button`
  padding: 10px ${p => p.theme.spacing.lg};
  background: linear-gradient(135deg, ${p => p.theme.colors.primary}, ${p => p.theme.colors.primaryDark});
  color: white; border: none;
  border-radius: ${p => p.theme.borderRadius.md};
  font-size: ${p => p.theme.fontSizes.sm}; font-weight: 600;
  font-family: ${p => p.theme.fonts.primary};
  display: flex; align-items: center; gap: 6px;
  cursor: pointer; transition: ${p => p.theme.transitions.fast};
  box-shadow: 0 4px 14px ${p => p.theme.colors.primary}40;
  &:hover { transform: translateY(-1px); }
`;

const FilterSection = styled.div`
  background: ${p => p.theme.colors.backgroundLight};
  border-radius: ${p => p.theme.borderRadius.lg};
  padding: ${p => p.theme.spacing.lg};
  margin-bottom: ${p => p.theme.spacing.xl};
  box-shadow: ${p => p.theme.shadows.card};
`;

const SearchWrap = styled.div`position: relative; max-width: 480px;`;
const SIcon = styled(MdSearch)`
  position: absolute; left: 12px; top: 50%; transform: translateY(-50%);
  color: ${p => p.theme.colors.textLight}; font-size: 1.2rem;
`;
const SearchInput = styled.input`
  width: 100%; padding: ${p => p.theme.spacing.md} ${p => p.theme.spacing.md} ${p => p.theme.spacing.md} 2.5rem;
  border: 1px solid ${p => p.theme.colors.border}; border-radius: ${p => p.theme.borderRadius.md};
  font-size: ${p => p.theme.fontSizes.sm}; background: ${p => p.theme.colors.background};
  color: ${p => p.theme.colors.textPrimary};
  &:focus { outline: none; border-color: ${p => p.theme.colors.primary}; }
  &::placeholder { color: ${p => p.theme.colors.textLight}; }
`;

const FilterButtons = styled.div`display: flex; gap: ${p => p.theme.spacing.sm}; margin-top: ${p => p.theme.spacing.md}; flex-wrap: wrap;`;
const FilterBtn = styled.button`
  padding: 6px 16px;
  background: ${p => p.active
    ? `linear-gradient(135deg, ${p.theme.colors.primary}, ${p.theme.colors.primaryDark})`
    : p.theme.colors.backgroundDark};
  color: ${p => p.active ? 'white' : p.theme.colors.textSecondary};
  border: none; border-radius: 99px;
  font-size: ${p => p.theme.fontSizes.sm}; font-weight: ${p => p.active ? 600 : 400};
  font-family: ${p => p.theme.fonts.primary}; cursor: pointer;
  transition: ${p => p.theme.transitions.fast};
  box-shadow: ${p => p.active ? `0 3px 10px ${p.theme.colors.primary}35` : 'none'};
  display: flex; align-items: center; gap: 5px;
  svg { font-size: 0.9rem; }
`;

const ListSection = styled.div`
  background: ${p => p.theme.colors.backgroundLight};
  border-radius: ${p => p.theme.borderRadius.lg};
  padding: ${p => p.theme.spacing.xl};
  box-shadow: ${p => p.theme.shadows.card};
`;

const SectionTitle = styled.h2`
  font-size: ${p => p.theme.fontSizes.xl};
  color: ${p => p.theme.colors.textPrimary};
  margin: 0 0 ${p => p.theme.spacing.lg} 0;
`;

const TableWrap = styled.div`overflow-x: auto; -webkit-overflow-scrolling: touch;`;
const Table = styled.table`width: 100%; border-collapse: collapse; min-width: 640px;`;
const Th = styled.th`
  padding: 8px ${p => p.theme.spacing.md};
  text-align: left; font-size: ${p => p.theme.fontSizes.xs}; font-weight: 700;
  color: ${p => p.theme.colors.textLight}; text-transform: uppercase; letter-spacing: 0.06em;
  border-bottom: 1px solid ${p => p.theme.colors.border};
`;
const Td = styled.td`
  padding: ${p => p.theme.spacing.md};
  font-size: ${p => p.theme.fontSizes.sm}; color: ${p => p.theme.colors.textPrimary};
  border-bottom: 1px solid ${p => p.theme.colors.borderLight};
`;

const NumLink = styled.button`
  font-size: ${p => p.theme.fontSizes.sm}; font-weight: 700;
  color: ${p => p.theme.colors.primary}; background: none; border: none;
  padding: 0; cursor: pointer;
  &:hover { text-decoration: underline; }
`;

const STATUS_CONFIG = {
  pendiente:   { label: 'Pendiente',   color: '#F6AD55', Icon: MdPendingActions },
  en_proceso:  { label: 'En proceso',  color: '#63B3ED', Icon: MdPlayArrow },
  completado:  { label: 'Completado',  color: '#68D391', Icon: MdCheckCircle },
  cancelado:   { label: 'Cancelado',   color: '#FC8181', Icon: MdCancel },
};

const StatusBadge = styled.span`
  display: inline-flex; align-items: center; gap: 4px;
  padding: 3px 10px; border-radius: 99px;
  font-size: ${p => p.theme.fontSizes.xs}; font-weight: 700;
  background: ${p => p.color}20; color: ${p => p.color};
  svg { font-size: 0.85rem; }
`;

const ViewBtn = styled.button`
  padding: 5px 14px;
  background: linear-gradient(135deg, ${p => p.theme.colors.primary}, ${p => p.theme.colors.primaryDark});
  color: white; border: none; border-radius: 8px;
  font-size: ${p => p.theme.fontSizes.xs}; font-weight: 600;
  font-family: ${p => p.theme.fonts.primary};
  display: inline-flex; align-items: center; gap: 4px; cursor: pointer;
  svg { font-size: 1rem; }
  &:hover { transform: translateY(-1px); box-shadow: 0 4px 10px ${p => p.theme.colors.primary}40; }
`;

const formatPrice = p => Number(p || 0).toLocaleString('es-MX', { style: 'currency', currency: 'MXN' });

const STATUSES = ['todos', 'pendiente', 'en_proceso', 'completado', 'cancelado'];

const Pedidos = () => {
  const navigate = useNavigate();
  const { toast } = useModal();
  const { config } = useConfig();
  const [pedidos, setPedidos] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [estadoFilter, setEstadoFilter] = useState('todos');

  useEffect(() => {
    loadAll();
  }, []); // eslint-disable-line

  const loadAll = async () => {
    try {
      setLoading(true);
      const pedRes = await pedidosAPI.getAll();
      setPedidos(pedRes.data.data || []);
    } catch (err) {
      toast(err.message || 'Error al cargar', 'error');
    } finally {
      setLoading(false);
    }
  };

  const tipoActual = config?.tipo_negocio === 'servicios' ? 'proyecto' : 'pedido';
  const isProyecto = tipoActual === 'proyecto';

  const filtered = pedidos.filter(p => {
    if (p.tipo !== tipoActual) return false;
    if (estadoFilter !== 'todos' && p.estado !== estadoFilter) return false;
    const q = search.toLowerCase();
    return !q ||
      p.numero?.toLowerCase().includes(q) ||
      (p.cliente_nombre_rel || p.cliente_nombre || '').toLowerCase().includes(q);
  });

  const countByEstado = (s) => pedidos.filter(p => p.tipo === tipoActual && p.estado === s).length;

  return (
    <Container>
      <PageHeader>
        <TitleGroup>
          <Title>
            {isProyecto ? <MdWorkspaces /> : <MdShoppingCart />}
            {isProyecto ? 'Proyectos' : 'Pedidos'}
          </Title>
          <Subtitle>{filtered.length} {isProyecto ? 'proyecto(s)' : 'pedido(s)'}</Subtitle>
        </TitleGroup>
        <AddBtn onClick={() => navigate('/pedidos/nuevo')}>
          <MdAdd /> {isProyecto ? 'Nuevo Proyecto' : 'Nuevo Pedido'}
        </AddBtn>
      </PageHeader>

      <FilterSection>
        <SearchWrap>
          <SIcon />
          <SearchInput
            placeholder={`Buscar por numero o cliente...`}
            value={search}
            onChange={e => setSearch(e.target.value)}
          />
        </SearchWrap>
        <FilterButtons>
          <FilterBtn active={estadoFilter === 'todos'} onClick={() => setEstadoFilter('todos')}>
            Todos ({pedidos.filter(p => p.tipo === tipoActual).length})
          </FilterBtn>
          {STATUSES.slice(1).map(s => {
            const cfg = STATUS_CONFIG[s];
            return (
              <FilterBtn key={s} active={estadoFilter === s} onClick={() => setEstadoFilter(s)}>
                <cfg.Icon /> {cfg.label} ({countByEstado(s)})
              </FilterBtn>
            );
          })}
        </FilterButtons>
      </FilterSection>

      <ListSection>
        <SectionTitle>{isProyecto ? 'Lista de Proyectos' : 'Lista de Pedidos'} ({filtered.length})</SectionTitle>
        <TableWrap>
          <Table>
            <thead>
              <tr>
                <Th>Numero</Th>
                <Th>Cliente</Th>
                <Th>Estado</Th>
                <Th>{isProyecto ? 'Inicio' : 'Fecha'}</Th>
                <Th>Entrega</Th>
                <Th>Total</Th>
                <Th>Accion</Th>
              </tr>
            </thead>
            <tbody>
              {loading ? (
                <SkeletonTableRows rows={6} cols={7} />
              ) : filtered.length > 0 ? (
                filtered.map(p => {
                  const st = STATUS_CONFIG[p.estado] || STATUS_CONFIG.pendiente;
                  return (
                    <tr key={p.id}>
                      <Td><NumLink onClick={() => navigate(`/pedidos/${p.id}`)}>{p.numero}</NumLink></Td>
                      <Td>{p.cliente_nombre_rel || p.cliente_nombre || 'N/A'}</Td>
                      <Td>
                        <StatusBadge color={st.color}>
                          <st.Icon /> {st.label}
                        </StatusBadge>
                      </Td>
                      <Td>{p.fecha_inicio ? new Date(p.fecha_inicio).toLocaleDateString('es-MX') : '—'}</Td>
                      <Td>{p.fecha_entrega ? new Date(p.fecha_entrega).toLocaleDateString('es-MX') : '—'}</Td>
                      <Td>{formatPrice(p.total)}</Td>
                      <Td>
                        <ViewBtn onClick={() => navigate(`/pedidos/${p.id}`)}>
                          <MdVisibility /> Ver
                        </ViewBtn>
                      </Td>
                    </tr>
                  );
                })
              ) : (
                <tr>
                  <td colSpan="7">
                    <EmptyState
                      type="clients"
                      title={search || estadoFilter !== 'todos' ? 'Sin resultados' : `Sin ${isProyecto ? 'proyectos' : 'pedidos'} aun`}
                      subtitle={search || estadoFilter !== 'todos'
                        ? 'Cambia el filtro o termino de busqueda.'
                        : `Crea tu primer ${isProyecto ? 'proyecto' : 'pedido'}.`}
                      actionLabel={!search && estadoFilter === 'todos' ? `Crear ${isProyecto ? 'Proyecto' : 'Pedido'}` : undefined}
                      onAction={!search && estadoFilter === 'todos' ? () => navigate('/pedidos/nuevo') : undefined}
                    />
                  </td>
                </tr>
              )}
            </tbody>
          </Table>
        </TableWrap>
      </ListSection>
    </Container>
  );
};

export default Pedidos;
